/**
 * SearchDrawer – manages the slide-in search panel.
 *
 * Opens when any [data-search-drawer-open] button is clicked.
 * Hides the "default state" (suggested / trending) while the user is typing
 * and restores it when the input is cleared.
 *
 * While closed the panel is inert + aria-hidden + visibility:hidden so screen
 * readers do not land on a separate "dialog, Search" stop after the Search
 * button. Enter on Search opens the drawer; focus moves into the input.
 */
class SearchDrawer extends HTMLElement {
  constructor() {
    super();

    this.panel = this.querySelector('#SearchDrawerPanel');
    this.overlay = this.querySelector('#SearchDrawerOverlay');
    this.closeBtn = this.querySelector('#SearchDrawerClose');
    this.input = this.querySelector('#SearchDrawer-Input');
    this.resetBtn = this.querySelector('.search-drawer__input-reset');
    this.defaultState = this.querySelector('#SearchDrawerDefault');
    this.predictiveEl = this.querySelector('predictive-search');
    this.form = this.querySelector('.search-drawer__form');
    this._queryCountCache = new Map();

    this._lastFocusedElement = null;
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onDocumentKeyDown = this._onDocumentKeyDown.bind(this);
  }

  connectedCallback() {
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    this.input?.addEventListener('input', () => this._onInput());
    this.form?.addEventListener('reset', () => {
      requestAnimationFrame(() => this._onInput());
    });

    this.addEventListener('keydown', (e) => this._onKeyDown(e));

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('is-animated');
      });
    });

    document.addEventListener('click', this._onTriggerClick);
    this._setExpanded(false);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onTriggerClick);
    document.removeEventListener('keydown', this._onDocumentKeyDown);
  }

  _onTriggerClick(event) {
    const trigger = event.target.closest('[data-search-drawer-open]');
    if (!trigger) return;
    event.preventDefault();
    this._lastFocusedElement = trigger;
    this.open();
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open() {
    if (this.isOpen) return;

    this._setExpanded(true);
    this.setAttribute('open', '');
    document.body.classList.add('search-drawer-open');
    document.addEventListener('keydown', this._onDocumentKeyDown);

    requestAnimationFrame(() => {
      this.input?.focus();
    });
    this._onInput();
  }

  close() {
    if (!this.isOpen) return;

    document.removeEventListener('keydown', this._onDocumentKeyDown);
    document.body.classList.remove('search-drawer-open');

    const trigger = this._lastFocusedElement;
    if (trigger && typeof trigger.focus === 'function') {
      trigger.focus();
    } else if (this.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    this._setExpanded(false);
    this.removeAttribute('open');

    if (this.input) {
      this.input.value = '';
    }

    if (this.predictiveEl && typeof this.predictiveEl.close === 'function') {
      this.predictiveEl.close(true);
    } else {
      this.form?.dispatchEvent(new Event('reset'));
    }
    this._showDefault();
  }

  /**
   * Keep the closed panel out of the accessibility tree, and reflect open
   * state on every Search trigger.
   */
  _setExpanded(isOpen) {
    if (this.panel) {
      this.panel.toggleAttribute('inert', !isOpen);
      if (isOpen) {
        this.panel.removeAttribute('aria-hidden');
      } else {
        this.panel.setAttribute('aria-hidden', 'true');
      }
    }

    document.querySelectorAll('[data-search-drawer-open]').forEach((button) => {
      button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  _onDocumentKeyDown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.close();
    }
  }

  /**
   * Handles Escape key and Focus Trapping while the drawer is open.
   */
  _onKeyDown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      this._handleFocusTrap(event);
    }
  }

  _handleFocusTrap(event) {
    const focusableElements = [...this.querySelectorAll(
      'button:not([disabled]), [href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((el) => !el.closest('[hidden], .hidden') && !el.hasAttribute('inert'));

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement.focus();
        event.preventDefault();
      }
    } else if (document.activeElement === lastElement) {
      firstElement.focus();
      event.preventDefault();
    }
  }

  _onInput() {
    const hasText = this.input?.value.trim().length > 0;
    if (hasText) {
      this._hideDefault();
      this._showReset();
    } else {
      this._showDefault();
      this._hideReset();
    }
  }

  _showDefault() {
    this.defaultState?.classList.remove('hidden');
  }

  _hideDefault() {
    this.defaultState?.classList.add('hidden');
  }

  _showReset() {
    this.resetBtn?.classList.remove('hidden');
  }

  _hideReset() {
    this.resetBtn?.classList.add('hidden');
  }
}

customElements.define('search-drawer', SearchDrawer);
